import { ref, computed } from 'vue'
import { defineStore } from 'pinia'

type ThemePreference = 'dark' | 'light' | 'system'

export const useSettingsStore = defineStore('settings', () => {
  const STORAGE_KEY = 'fos-theme'

  const theme = ref<ThemePreference>(
    (localStorage.getItem(STORAGE_KEY) as ThemePreference) ?? 'system',
  )

  let systemMq: MediaQueryList | null = null
  let systemListener: (() => void) | null = null

  const resolvedTheme = computed<'dark' | 'light'>(() => {
    if (theme.value === 'system') {
      return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
    }
    return theme.value
  })

  function applyTheme() {
    const resolved = resolvedTheme.value
    document.documentElement.classList.toggle('dark', resolved === 'dark')
    document.documentElement.classList.toggle('light', resolved === 'light')
  }

  function attachSystemListener() {
    if (systemListener) return
    systemMq = window.matchMedia('(prefers-color-scheme: dark)')
    systemListener = () => applyTheme()
    systemMq.addEventListener('change', systemListener)
  }

  function detachSystemListener() {
    if (systemMq && systemListener) {
      systemMq.removeEventListener('change', systemListener)
      systemMq = null
      systemListener = null
    }
  }

  function setTheme(t: ThemePreference) {
    theme.value = t
    localStorage.setItem(STORAGE_KEY, t)
    if (t === 'system') {
      attachSystemListener()
    } else {
      detachSystemListener()
    }
    applyTheme()
  }

  // Init: apply stored preference and attach system listener if needed
  if (theme.value === 'system') {
    attachSystemListener()
  }

  return { theme, resolvedTheme, setTheme, applyTheme }
})
